import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, Clock, ShieldCheck, Users, MessageSquare, Flag, ScrollText, CheckCircle2, XCircle, EyeOff } from "lucide-react";
import { api, priceLabel, fmtDate } from "@/lib/loop";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { VerificationBadge } from "@/components/Badges";
import { toast } from "sonner";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState({});
  const [props, setProps] = useState([]);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [reports, setReports] = useState([]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/properties").then((r) => setProps(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/audit").then((r) => setAudit(r.data));
    api.get("/admin/reports").then((r) => setReports(r.data));
  };
  useEffect(() => { load(); }, []);

  const reject = async (id) => { await api.post(`/admin/properties/${id}/reject`, { note: "Rejected via admin" }); toast("Listing rejected"); load(); };
  const suspend = async (id) => { await api.post(`/admin/properties/${id}/suspend`); toast("Listing suspended"); load(); };
  const verifyUser = async (id) => { await api.post(`/admin/users/${id}/verify`); toast.success("User verified"); load(); };

  const stat = (label, val, Icon, tone = "text-white") => (
    <div className="bg-[#0B132B] border border-white/10 rounded-2xl p-5">
      <Icon className="w-5 h-5 text-[#C5A059]" />
      <div className={`mt-3 text-2xl font-extrabold ${tone}`}>{val ?? 0}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8 pb-28 md:pb-16">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">Admin Control</div>
      <h1 className="mt-1 text-2xl font-bold text-white">LOOP Moderation & Verification</h1>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stat("Total properties", stats.total_properties, LayoutGrid)}
        {stat("Pending verification", stats.pending_verification, Clock, "text-amber-400")}
        {stat("Verified", stats.verified, ShieldCheck, "text-emerald-400")}
        {stat("Open reports", stats.reports, Flag, "text-rose-400")}
      </div>

      <Tabs defaultValue="listings" className="mt-8">
        <TabsList className="bg-[#0B132B] border border-white/10 flex-wrap h-auto">
          {[["listings", "Listings", LayoutGrid], ["users", "Users", Users], ["reports", "Reports", Flag], ["audit", "Audit Trail", ScrollText]].map(([v, l, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#070C1E] text-slate-300">
              <Icon className="w-4 h-4 mr-1.5" /> {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="listings" className="mt-6 space-y-3">
          {props.map((p) => (
            <div key={p.id} data-testid={`admin-property-${p.loop_id}`} className="bg-[#0B132B] border border-white/10 rounded-2xl p-4 flex flex-col lg:flex-row gap-4 lg:items-center">
              <img src={p.images?.[0]} alt="" className="w-full lg:w-28 h-20 object-cover rounded-lg cursor-pointer" onClick={() => nav(`/property/${p.id}`)} />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono-loop text-xs text-slate-500">{p.loop_id}</span>
                  <VerificationBadge status={p.verification_status} />
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${p.moderation === "verification_pending" ? "bg-amber-500/15 text-amber-400" : p.moderation === "rejected" ? "bg-rose-500/15 text-rose-400" : "bg-emerald-500/15 text-emerald-400"}`}>{p.moderation?.replace(/_/g, " ")}</span>
                </div>
                <div className="text-sm font-semibold text-white mt-1">{p.title}</div>
                <div className="text-xs text-slate-500">{priceLabel(p)} · {p.owner_name}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <ApproveDialog property={p} onDone={load} />
                <Button data-testid={`reject-${p.loop_id}`} size="sm" variant="outline" onClick={() => reject(p.id)} className="border-rose-500/30 text-rose-400 text-xs"><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                <Button data-testid={`suspend-${p.loop_id}`} size="sm" variant="ghost" onClick={() => suspend(p.id)} className="text-slate-400 text-xs"><EyeOff className="w-3.5 h-3.5 mr-1" /> Suspend</Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="users" className="mt-6 space-y-2">
          {users.map((u) => (
            <div key={u.id} className="bg-[#0B132B] border border-white/10 rounded-xl p-4 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-white">{u.name} <span className="text-xs text-slate-500 capitalize">· {u.role}</span></div>
                <div className="text-xs text-slate-500">{u.email}</div>
              </div>
              {u.verified ? <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Verified</span>
                : <Button data-testid={`verify-user-${u.id}`} size="sm" onClick={() => verifyUser(u.id)} className="bg-[#C5A059] text-[#070C1E] text-xs">Verify</Button>}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="reports" className="mt-6 space-y-2">
          {reports.length === 0 ? <div className="text-slate-500 py-10 text-center">No reports.</div> :
            reports.map((r) => (
              <div key={r.id} className="bg-[#0B132B] border border-white/10 rounded-xl p-4">
                <div className="text-sm font-semibold text-rose-400">{r.reason}</div>
                {r.detail && <div className="text-sm text-slate-400 mt-1">{r.detail}</div>}
                <div className="text-xs text-slate-600 mt-1">{fmtDate(r.created_at)}</div>
              </div>
            ))}
        </TabsContent>

        <TabsContent value="audit" className="mt-6 space-y-2">
          {audit.length === 0 ? <div className="text-slate-500 py-10 text-center">No admin actions logged yet.</div> :
            audit.map((a) => (
              <div key={a.id} className="bg-[#0B132B] border border-white/10 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="text-sm text-white"><span className="text-[#C5A059] font-mono-loop">{a.action}</span> → {a.target}</div>
                  <div className="text-xs text-slate-500">{a.admin_email} {a.note && `· ${a.note}`}</div>
                </div>
                <div className="text-xs text-slate-600">{fmtDate(a.created_at)}</div>
              </div>
            ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ApproveDialog({ property, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    verification_status: "documents_pending", owner_status: property.owner_status,
    location_status: property.location_status, document_status: property.document_status, note: "",
  });
  const approve = async () => {
    await api.post(`/admin/properties/${property.id}/approve`, form);
    toast.success("Listing approved & published");
    setOpen(false); onDone();
  };
  const field = (key, label, opts) => (
    <div>
      <Label className="text-xs text-slate-400">{label}</Label>
      <Select value={form[key]} onValueChange={(v) => setForm({ ...form, [key]: v })}>
        <SelectTrigger className="mt-1 bg-[#131B35] border-white/10 text-white" data-testid={`approve-${key}`}><SelectValue /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`approve-${property.loop_id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Review & Approve</Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0B132B] border-white/10 text-white">
        <DialogHeader><DialogTitle>Verify {property.loop_id}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Set only what LOOP actually checked. Nothing is auto-approved.</p>
        <div className="grid grid-cols-2 gap-3">
          {field("verification_status", "Verification", [["verified", "Verified"], ["documents_pending", "Documents Pending"], ["unverified", "Unverified"]])}
          {field("owner_status", "Owner", [["verified", "Identity Verified"], ["pending", "Pending"]])}
          {field("location_status", "Location", [["verified", "Verified"], ["approximate", "Approximate"]])}
          {field("document_status", "Documents", [["complete", "Complete"], ["partial", "Partial"], ["pending", "Pending"]])}
        </div>
        <DialogFooter><Button data-testid="approve-confirm" onClick={approve} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full">Approve & Publish</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
